import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../../../hooks/api";
import { Col } from "react-bootstrap";
import { useAppSelector } from "../../../../redux/hooks";
import { TextField } from "../../../fields/base/TextField";
import { css } from "../../../../../styled-system/css";
import { FaSpinner } from "react-icons/fa";

const AssetSummary = ({
  assetId,
  // Description of the assets purpose/context within the dashboard, not of individual asset content (i.e. Lifestyle Image, Video Creative, etc.)
  assetDescriptor,
  assetType,
  // Below for override if unique endpoint to fetch from (neither assets or uploaded videos)
  assetURL = null,
  // Any child props for rendering beneath main asset display
  hidden,
  summaryContainerClasses,
  columnSpan = 6,
  displayAssetName = true,
}) => {
  const { t } = useTranslation(["advertising"]);
  const [isMediaLoading, setIsMediaLoading] = useState(true);
  const [isMediaError, setIsMediaError] = useState(false);

  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { data: selectedAsset, isLoading: isAssetLoading } = useFetch(
    ["assetSearch", assetId],
    "/assets/",
    {
      asset_id: assetId,
      marketplace: marketPlace,
    },
    {
      select: (res) => res.data?.results?.[0],
      default: [],
      enabled: !!assetId,
    },
  );

  const { data: videoData, isLoading: isVideoLoading } = useFetch(
    ["trellisVideo", assetId],
    "/creatives/media/video",
    {
      id: assetId,
    },
    {
      default: [],
      // Only use in videos created on the Trellis platform
      enabled: !!assetId && !isNaN(parseInt(assetId)),
      select: (res) => res.data,
    },
  );

  const mediaSrc = assetURL ?? videoData?.url ?? selectedAsset?.url_default;
  const noMediaResolved = !mediaSrc && !isAssetLoading && !isVideoLoading;
  const showSpinner =
    !isMediaError && ((!mediaSrc && (isAssetLoading || isVideoLoading)) || (Boolean(mediaSrc) && isMediaLoading));

  const mediaStyle = { width: "100%", maxHeight: "300px" };

  if (hidden) {
    return null;
  }

  return (
    <Col xs={12} lg={columnSpan} className={`${summaryContainerClasses ? summaryContainerClasses : ""}`}>
      <h6
        className="title mb-4 fs-4"
        style={{
          color: "#73706E",
          fontWeight: "700",
          textTransform: "none",
        }}
      >
        {assetDescriptor}
      </h6>
      {displayAssetName && (
        <TextField
          value={selectedAsset?.name}
          isDisabled
          aria-label={t(($) => $.advertising["Asset Name"])}
          css={{ marginBottom: "small" }}
        />
      )}
      <div style={{ position: "relative" }}>
        {showSpinner && (
          <div
            style={{
              ...mediaStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaSpinner className="fa-spin" />
          </div>
        )}
        {noMediaResolved ?
          <div
            style={{
              ...mediaStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.8,
            }}
          >
            <span>{t(($) => $.advertising["Unable to load media."])}</span>
          </div>
        : null}
        {mediaSrc &&
          (assetType === "VIDEO" ?
            <video
              controls
              className={css({ width: "100%", maxHeight: "300px", objectFit: "fill" })}
              preload={"auto"}
              controlsList={"nodownload noremoteplayback"}
              disablePictureInPicture
              key={`${assetType}-${mediaSrc}`}
              onLoadedData={() => {
                setIsMediaLoading(false);
                setIsMediaError(false);
              }}
              onError={() => {
                setIsMediaLoading(false);
                setIsMediaError(true);
              }}
              style={{ opacity: isMediaLoading && !isMediaError ? 0 : 1 }}
            >
              <source src={mediaSrc} type="video/mp4" />
            </video>
          : <img
              className="w-100"
              src={mediaSrc}
              alt={`${selectedAsset?.name}`}
              key={`${assetType}-${mediaSrc}`}
              style={{ maxHeight: "300px", objectFit: "contain", opacity: isMediaLoading && !isMediaError ? 0 : 1 }}
              onLoad={() => {
                setIsMediaLoading(false);
                setIsMediaError(false);
              }}
              onError={() => {
                setIsMediaLoading(false);
                setIsMediaError(true);
              }}
            />)}
      </div>
    </Col>
  );
};

export default AssetSummary;
